import React, { useState, useEffect, useRef } from "react";
import { IBreed, IEventSummary, IShow } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { apiService } from "src/services/api.service";

import Button from "src/components/Button/Button";
import Chip from "src/components/Chip/Chip";
import DatePick from "src/components/DatePick/DatePick";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import SearchDown from "src/components/SearchDown/SearchDown";
import { toast } from "src/components/Toast/ToastManager";

import styles from "./BreedsTab.module.scss";

interface IProps {
  event: IEventSummary;
  show: IShow;
  onUpdate(): void;
}

const emptyGroupOption: IOption = { id: "", value: "Select a group" };

const groupOrderForClub = (sanctioningClub: string): readonly string[] =>
  sanctioningClub === Constants.sanctioning_club.UKC
    ? Constants.ukc_breed_group_order
    : Constants.akc_breed_group_order;

const breedsInGroup = (breeds: IBreed[], sanctioningClub: string, group: string): string[] => {
  const names = new Set<string>();
  for (const breed of breeds) {
    const matches = (breed.groups || []).some(
      (entry) => entry.name === sanctioningClub && entry.value === group
    );
    if (matches && breed.breed) names.add(breed.breed);
  }
  return [...names].sort((left, right) => left.localeCompare(right));
};

const BreedsTab: React.FC<IProps> = (props) => {
  const [breeds, setBreeds] = useState<IBreed[]>([]);
  const [breedSearch, setBreedSearch] = useState<string>("");
  const [breedResults, setBreedResults] = useState<string[]>([]);
  const [mode, setMode] = useState<string>(
    props.show.breed_restriction_mode ?? Constants.breed_restriction_mode.exclude
  );
  const [excludedBreeds, setExcludedBreeds] = useState<string[]>(
    props.show.excluded_breeds ? [...props.show.excluded_breeds] : []
  );
  const [allowedBreeds, setAllowedBreeds] = useState<string[]>(
    props.show.allowed_breeds ? [...props.show.allowed_breeds] : []
  );
  const [excludedEndDate, setExcludedEndDate] = useState<Date | null>(
    (props.show as any).excluded_breeds_end_date
      ? new Date((props.show as any).excluded_breeds_end_date)
      : null
  );
  const [allowedEndDate, setAllowedEndDate] = useState<Date | null>(
    (props.show as any).allowed_breeds_end_date
      ? new Date((props.show as any).allowed_breeds_end_date)
      : null
  );
  const [saving, setSaving] = useState<boolean>(false);
  const [groupChoice, setGroupChoice] = useState<IOption>(emptyGroupOption);
  const searchDownRef = useRef<HTMLDivElement>(null);

  const showGroupLimit =
    props.show.show_type === Constants.show_type.conformation &&
    (props.event.sanctioning_club === Constants.sanctioning_club.AKC ||
      props.event.sanctioning_club === Constants.sanctioning_club.UKC);

  const isExcludeMode = mode === Constants.breed_restriction_mode.exclude;
  const activeList = isExcludeMode ? excludedBreeds : allowedBreeds;
  const setActiveList = isExcludeMode ? setExcludedBreeds : setAllowedBreeds;

  const getBreedListClub = (sanctioningClub: string): string => {
    switch (sanctioningClub) {
      case Constants.sanctioning_club.AKC:
      case Constants.sanctioning_club.BHA:
      case Constants.sanctioning_club.ASFA:
        return Constants.sanctioning_club.AKC;
      case Constants.sanctioning_club.UKC:
      case Constants.sanctioning_club.GENERAL:
      case Constants.sanctioning_club.C_WAGS:
        return Constants.sanctioning_club.UKC;
      default:
        return sanctioningClub;
    }
  };

  useEffect(() => {
    const breedListClub = getBreedListClub(props.event.sanctioning_club);
    apiService.getBreedsBySanctioningClub(breedListClub)
      .then((data: IBreed[]) => {
        setBreeds(data);
      })
      .catch(() => {
        apiService.getBreeds().then((data: IBreed[]) => {
          setBreeds(data);
        }).catch(() => {});
      });
  }, [props.event.sanctioning_club]);

  const handleBreedSearch = (value: string) => {
    setBreedSearch(value);
    if (!value.trim()) {
      setBreedResults([]);
      return;
    }
    const lower = value.toLowerCase();
    const results = breeds
      .filter((b) => b.breed.toLowerCase().includes(lower))
      .map((b) => b.breed)
      .slice(0, 20);
    setBreedResults(results);
  };

  const handleBreedSelect = (breedName: string) => {
    setBreedSearch("");
    setBreedResults([]);

    if (activeList.includes(breedName)) {
      toast.show({
        title: "Breed Restrictions",
        content: `${breedName} is already in the list`,
        duration: 5000,
        type: "fail",
      });
      return;
    }

    setActiveList((prev) => [...prev, breedName]);
  };

  const removeBreed = (index: number) => {
    setActiveList((prev) => prev.filter((_, i) => i !== index));
  };

  const groupOptions: IOption[] = groupOrderForClub(props.event.sanctioning_club)
    .filter((group) => breedsInGroup(breeds, props.event.sanctioning_club, group).length > 0)
    .map((group) => ({ id: group, value: group }));

  const handleGroupSelect = (_event: unknown, option: IOption) => {
    if (!option.id) return;
    const names = breedsInGroup(breeds, props.event.sanctioning_club, option.id);
    if (names.length === 0) {
      toast.show({
        title: "Breed Restrictions",
        content: `No ${option.value} breeds were found`,
        duration: 5000,
        type: "fail",
      });
      setGroupChoice(emptyGroupOption);
      return;
    }
    setMode(Constants.breed_restriction_mode.allow);
    setAllowedBreeds(names);
    setBreedSearch("");
    setBreedResults([]);
    setGroupChoice(emptyGroupOption);
    toast.show({
      title: "Breed Restrictions",
      content: `${names.length} ${option.value} breeds are now the allow list. Save to apply this restriction.`,
      duration: 5000,
      type: "success",
    });
  };

  const handleModeChange = (newMode: string) => {
    setMode(newMode);
    setBreedSearch("");
    setBreedResults([]);
  };

  const handleSave = () => {
    setSaving(true);

    const { __v, ...showWithoutVersion } = props.show as any;
    const payload: any = {
      ...showWithoutVersion,
      breed_restriction_mode: mode,
      excluded_breeds: excludedBreeds,
      excluded_breeds_end_date: excludedEndDate ?? null,
      allowed_breeds: allowedBreeds,
      allowed_breeds_end_date: allowedEndDate ?? null,
    };

    apiService.updateShow(props.event._id, props.show._id, payload)
      .then(() => {
        toast.show({
          title: "Breed Restrictions",
          content: "Breed restrictions have been saved successfully",
          duration: 5000,
          type: "success",
        });
        props.onUpdate();
      })
      .catch(() => {
        toast.show({
          title: "Breed Restrictions",
          content: "Failed to save breed restrictions",
          duration: 5000,
          type: "fail",
        });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div className={styles.BreedsTab}>
      <div className={styles.content}>
        <div className={styles.modeToggle}>
          <label
            className={`${styles.modeOption} ${isExcludeMode ? styles.active : ""}`}
            onClick={() => handleModeChange(Constants.breed_restriction_mode.exclude)}
          >
            <input
              type="radio"
              name="breedMode"
              value={Constants.breed_restriction_mode.exclude}
              checked={isExcludeMode}
              onChange={() => handleModeChange(Constants.breed_restriction_mode.exclude)}
            />
            <span>Exclude specific breeds</span>
          </label>
          <label
            className={`${styles.modeOption} ${!isExcludeMode ? styles.active : ""}`}
            onClick={() => handleModeChange(Constants.breed_restriction_mode.allow)}
          >
            <input
              type="radio"
              name="breedMode"
              value={Constants.breed_restriction_mode.allow}
              checked={!isExcludeMode}
              onChange={() => handleModeChange(Constants.breed_restriction_mode.allow)}
            />
            <span>Only allow specific breeds</span>
          </label>
        </div>

        <p>
          {isExcludeMode
            ? "Dogs registered under an excluded breed will be blocked from completing registration."
            : "Only dogs registered under an allowed breed will be able to complete registration. All other breeds will be blocked."}
        </p>

        {showGroupLimit && (
          <div className={styles.groupRow}>
            <Dropdown
              label="Limit to one group"
              placeholder="Select a group"
              options={groupOptions}
              value={groupChoice}
              disabled={breeds.length === 0}
              onChange={handleGroupSelect}
            />
            <p className={styles.groupHint}>
              Choosing a group switches to the allow list and fills it with that group’s breeds as of now. Save to apply it. You can still add or remove breeds afterward.
            </p>
          </div>
        )}

        <div className={styles.endDateRow}>
          <DatePick
            label="End Date & Time (optional)"
            value={isExcludeMode ? excludedEndDate : allowedEndDate}
            onChange={(date: Date | null) => isExcludeMode ? setExcludedEndDate(date) : setAllowedEndDate(date)}
            showTimeSelect={true}
            isClearable={true}
            placeholder="MM/DD/YYYY HH:MM AM"
          />
          <p className={styles.endDateHint}>
            If set, this restriction will no longer be enforced after this date and time.
          </p>
        </div>

        <div className={styles.searchRow}>
          <div className={styles.searchWrap} ref={searchDownRef}>
            <SearchDown
              label="Search Breeds"
              placeholder="Type a breed name..."
              defaultValue={breedSearch}
              onSearch={handleBreedSearch}
              searchResults={breedResults}
              onSelect={handleBreedSelect}
              onClear={() => {
                setBreedSearch("");
                setBreedResults([]);
              }}
              showClear={true}
            />
          </div>
        </div>

        {activeList.length > 0 && (
          <div className={styles.listHeader}>
            <span>{activeList.length} breeds</span>
            <Button variant="secondary" onClick={() => setActiveList([])}>
              Clear list
            </Button>
          </div>
        )}

        <div className={styles.chipList}>
          {activeList.map((breed, index) => (
            <Chip key={index} onRemove={() => removeBreed(index)}>
              {breed}
            </Chip>
          ))}
          {activeList.length === 0 && (
            <div className={styles.emptyList}>
              {isExcludeMode
                ? "No breeds have been excluded"
                : "No breeds added — all breeds are currently allowed"}
            </div>
          )}
        </div>
      </div>

      <div className={styles.actions}>
        <Button isLoading={saving} onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default BreedsTab;
